'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { MoneyValue } from './commerceSource';

/**
 * Host adapter contract for the marketplace blocks — course-bundle
 * catalog, per-catalog course grid, cart, coupons, orders and payment
 * redirect. Mirrors {@link ./commerceSource} exactly (a host mounts
 * {@link MarketplaceSourceProvider} once near the root; a consuming block
 * calls {@link useMarketplaceSource}, gets `null` when nothing is mounted,
 * and renders its own local/demo state instead of throwing — "no backend
 * wired yet" stays a normal state, not an error).
 *
 * <p>The shape here follows akademi-lms-v2's real
 * `service/index.js` marketplace functions one-to-one
 * (`searchMarketplaceCatalog`, `getShoppingCart`, `addToShoppingCart`,
 * `applyShoppingCartCoupon`, `searchPurchaseOrders`,
 * `getPaymentGatewayRedirectUrl`, ...) so a host adapter implementing this
 * against that same backend is a thin translation layer, not a redesign.
 *
 * <p>Two domains from that source app are deliberately NOT modelled here:
 * address selection and payment-method choice. Neither has a
 * marketplace-scoped endpoint in `service/index.js` — the source app's
 * cart page borrows a generic employee/address search and reads payment
 * gateways off account config, both outside this module's concern. Until
 * a real marketplace-scoped contract exists for either,
 * `MarketplaceAddressSelect` / `MarketplacePaymentMethod` stay
 * authored-props-only blocks.
 */

export interface MarketplaceCatalogCard {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  /** Discriminates a genuine multi-course bundle from a standalone course
   *  surfaced through the same `listCatalogs` search — the two are
   *  visually and navigationally different (a bundle's `publicPath` is its
   *  own catalog-detail page; a course's is the plain course-detail page),
   *  so a block needs to tell them apart to render/link each correctly.
   *  The course-only fields below are only meaningful when this is
   *  `'course'`. */
  kind: 'catalog' | 'course';
  courseCount?: number;
  /** "Starting from" price for a bundle — akademi-lms-v2's `from.price`. */
  fromPrice?: MoneyValue;
  /** Pre-discount `from.listPrice` — present alongside `fromPrice` when the
   *  catalog is on sale, struck through next to it. */
  fromListPrice?: MoneyValue;
  freeItem?: boolean;
  /** How long access lasts after purchase, e.g. `365` + `"days"` —
   *  akademi-lms-v2's `validity` + `timeUnit`, shown as a badge next to the
   *  course count on the source app's catalog-detail page. */
  validity?: number;
  timeUnit?: string;
  /** The catalog's own long-form HTML write-up — akademi-lms-v2's `body`,
   *  distinct from the short `description` shown next to the title. */
  body?: string;
  /** Institution/provider name — akademi-lms-v2's `provider.name`. */
  provider?: string;
  providerLogo?: string;
  publicPath: string;
  inCart?: boolean;

  // --- Course-only fields (set when `kind === 'course'`) — mirrors
  // MarketplaceCourseCard's shape so a standalone course surfaced here
  // renders with the same fidelity as one reached via `listCatalogCourses`.
  /** Small pill on the tile's image, e.g. "E-Öğrenme" / "Program". */
  kindLabel?: string;
  kindIcon?: 'elearning' | 'program' | 'classroom' | 'video';
  attendeeCount?: number;
  likeCount?: number;
  dislikeCount?: number;
  /** ISO start/end for a live/classroom session. */
  startDate?: string;
  endDate?: string;
  /** Display string ("Beginner", "İleri Seviye", …). */
  level?: string;
}

export interface MarketplaceCatalogListing {
  items: MarketplaceCatalogCard[];
  total: number;
}

/** Facet filters for {@link MarketplaceSource.listCatalogs} — mirrors the
 *  real backend's `catalog/search` filter fields one-to-one so a host
 *  adapter can pass them straight through. */
export interface MarketplaceCatalogListingQuery {
  firstResult?: number;
  maxResults?: number;
  searchTerm?: string;
  sortField?: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
  /** LMS course-category ids (the same taxonomy `CategoryTree` with
   *  `feedSource: "course"` renders). */
  categoryIds?: Array<number | string>;
  /** `PUBLIC` | `BEGINNER` | `INTERMEDIATE` | `ADVANCED`. */
  courseLevels?: string[];
  /** `F2F_COURSE` | `REMOTE_COURSE` | `PROGRAM`. */
  courseTypes?: string[];
  /** `true` → free only, `false` → paid only, omit → both. */
  marketplaceFreeItems?: boolean;
}

export interface MarketplaceCourseCard {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  category?: string;
  /** Small pill on the tile's image, e.g. "E-Öğrenme" / "Program". */
  kindLabel?: string;
  kindIcon?: 'elearning' | 'program' | 'classroom' | 'video';
  attendeeCount?: number;
  likeCount?: number;
  dislikeCount?: number;
  /** ISO start/end for a live/classroom session — the tile shows a
   *  "Başlangıç Tarihi / Bitiş Tarihi" pair. */
  startDate?: string;
  endDate?: string;
  /** Institution / author line shown in the card's bottom bar. */
  provider?: string;
  providerLogo?: string;
  /** Display string ("Beginner", "İleri Seviye", …) — the `marketplace`
   *  card style shows this next to the like/dislike meta row. */
  level?: string;
  price?: MoneyValue;
  listPrice?: MoneyValue;
  freeItem?: boolean;
  purchased?: boolean;
  publicPath: string;
  inCart?: boolean;
}

export interface MarketplaceCourseListingQuery {
  q?: string;
  category?: string;
  firstResult?: number;
  maxResults?: number;
}

export interface MarketplaceCourseListing {
  items: MarketplaceCourseCard[];
  total: number;
}

export interface MarketplaceCartItem {
  cartItemId: string;
  marketplaceItemId: string;
  title: string;
  thumbnailUrl?: string;
  publicPath?: string;
  price?: MoneyValue;
  listPrice?: MoneyValue;
  freeItem?: boolean;
}

export interface MarketplaceAppliedCoupon {
  couponId: string;
  hashId: string;
}

export interface MarketplaceCart {
  items: MarketplaceCartItem[];
  coupons: MarketplaceAppliedCoupon[];
  /** Sum of every item's list price — computed client-side in the source
   *  app; carried here so a host can echo the same figure without the
   *  block re-deriving it. */
  totalListPrice?: MoneyValue;
  /** Sum of (list price − price) across items. */
  totalDiscount?: MoneyValue;
  /** Server-computed subtotal after coupon(s), before rounding to `total`. */
  subtotal?: MoneyValue;
  /** Final amount due. */
  total?: MoneyValue;
  paymentGateways?: string[];
}

export type MarketplaceOrderStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED' | (string & {});

export interface MarketplaceOrder {
  id: string;
  status: MarketplaceOrderStatus;
  total?: MoneyValue;
  createdAt?: string;
  items?: {
    title: string;
    thumbnailUrl?: string;
    /** The item's marketplace/course detail page — same rule as a cart
     *  line's own `publicPath` (a catalog/bundle goes to
     *  `/marketplace/catalog/{id}`, a single course to its own detail
     *  page). Absent when the source entity is gone (e.g. deleted). */
    href?: string;
    price?: MoneyValue;
    listPrice?: MoneyValue;
    free?: boolean;
  }[];
  /** Gateways this specific order can still be paid with — only meaningful
   *  while `status` isn't a paid/approved terminal state. Akademi-lms-v2's
   *  `enabledPaymentGateways`; drives the "pay with…" buttons on
   *  `/marketplace/orders/[id]`. */
  enabledPaymentGateways?: string[];
}

export interface MarketplaceOrderListingQuery {
  status?: MarketplaceOrderStatus;
  firstResult?: number;
  maxResults?: number;
}

export interface MarketplaceOrderListing {
  items: MarketplaceOrder[];
  total: number;
}

/** Bookmark lists an item can be toggled in/out of — mirrors the portal's
 *  own `CourseBookmarkKind` values one-to-one. */
export type MarketplaceBookmarkKind = 'watch-later' | 'favourites';

export interface MarketplaceSource {
  /** `POST /marketplace/catalog/search` — the top-level marketplace
   *  listing. Returns a mix of genuine multi-course bundles AND standalone
   *  courses sold on their own; each item's `kind` says which. */
  listCatalogs(query?: MarketplaceCatalogListingQuery): Promise<MarketplaceCatalogListing>;

  /** `GET /marketplace/catalog/{id}` — one catalog/bundle, or `null` when it
   *  doesn't exist. */
  getCatalog(id: string): Promise<MarketplaceCatalogCard | null>;

  /** `POST /catalog/search` with `filter.marketplaceCatalogIds = [catalogId]`
   *  and `filter.searchTerm` — the courses inside one bundle, exactly as
   *  akademi-lms-v2's catalog-detail page queries them. */
  listCatalogCourses(
    catalogId: string,
    query?: MarketplaceCourseListingQuery,
  ): Promise<MarketplaceCourseListing>;

  /** `GET /marketplace/cart`. */
  getCart(): Promise<MarketplaceCart>;

  /** `POST /marketplace/cart` — adds one licence of `marketplaceItemId`
   *  (a course or a catalog/bundle id). Returns the updated cart. */
  addToCart(marketplaceItemId: string): Promise<MarketplaceCart>;

  /** `DELETE /marketplace/cart/{cartItemId}`. Returns the updated cart. */
  removeFromCart(cartItemId: string): Promise<MarketplaceCart>;

  /** `PUT /marketplace/cart/coupon/apply`. Returns the updated cart. */
  applyCoupon(hashId: string): Promise<MarketplaceCart>;

  /** `DELETE /marketplace/cart/coupon/{couponId}`. Returns the updated cart. */
  removeCoupon(couponId: string): Promise<MarketplaceCart>;

  /** `POST /marketplace/purchase-order/search`. */
  listOrders(query?: MarketplaceOrderListingQuery): Promise<MarketplaceOrderListing>;

  /** `GET /marketplace/purchase-order/{id}`, or `null` when it doesn't exist. */
  getOrder(id: string): Promise<MarketplaceOrder | null>;

  /** `DELETE /marketplace/purchase-order/cancel/{id}`. */
  cancelOrder(id: string): Promise<void>;

  /** Toggle the viewer's watch-later/favourites membership for one item —
   *  akademi-lms-v2's course bookmark endpoint (a single idempotent call
   *  that flips membership; there's no separate remove verb). Used by
   *  `MarketplaceCoursePurchaseBox`'s wishlist buttons. */
  toggleBookmark(itemId: string, kind: MarketplaceBookmarkKind): Promise<void>;

  /** `POST /marketplace/payment/{gateway}/create-payment-gateway-url`.
   *  Stripe returns a hosted-checkout `url`; the other gateways return a
   *  `purchaseOrderId` the caller routes to `/marketplace/payment/...` or
   *  `/marketplace/orders/...` — mirrors the source app's branching.
   *  `bank_transfer` returns account details to transfer to (`bankDetails`
   *  + an EFT `reference` code) instead of a redirect target — pass the
   *  order's `purchaseOrderId` back in to re-fetch those details when
   *  resuming an existing (already-created) bank-transfer order, e.g. on
   *  `/marketplace/payment/bank_transfer/[purchaseOrderId]`. */
  getPaymentGatewayRedirectUrl(
    gateway: string,
    purchaseOrderId?: string,
  ): Promise<{
    url?: string;
    purchaseOrderId?: string;
    bankDetails?: MarketplaceBankTransferDetails;
    reference?: string;
    /** The order this payment is for — the same response carries it, so
     *  callers (e.g. the bank-transfer page) don't need a second
     *  `getOrder` round-trip just to show its status/items/total. */
    order?: MarketplaceOrder;
  }>;
}

/** Bank-account details to transfer to — `bank_transfer` gateway only. */
export interface MarketplaceBankTransferDetails {
  bank?: string;
  bankLogoUrl?: string;
  iban?: string;
  accountNumber?: string;
  sortCode?: string;
  receiver?: string;
}

const MarketplaceSourceContext = createContext<MarketplaceSource | null>(null);

export function MarketplaceSourceProvider({
  value,
  children,
}: {
  value: MarketplaceSource;
  children: ReactNode;
}) {
  return (
    <MarketplaceSourceContext.Provider value={value}>{children}</MarketplaceSourceContext.Provider>
  );
}

/** The host-provided source, or `null` when no provider is mounted — every
 *  marketplace block treats `null` as "render local/demo state", not an
 *  error. */
export function useMarketplaceSource(): MarketplaceSource | null {
  return useContext(MarketplaceSourceContext);
}
